import styled, { css, keyframes } from 'styled-components'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 16px;
  position: relative;
  overflow: hidden;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.textPrimary};
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, rgba(62, 207, 142, 0.18), transparent 55%),
    radial-gradient(ellipse 50% 40% at 100% 100%, rgba(62, 207, 142, 0.08), transparent 50%),
    ${({ theme }) => theme.colors.main};
`

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  padding: 24px 28px 20px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: 0 16px 40px rgba(15, 23, 42, ${({ theme }) => (theme.name === 'dark' ? 0.35 : 0.08)});
  animation: ${fadeUp} 0.4s ease-out both;

  @media (max-width: 480px) {
    padding: 20px 20px 16px;
  }
`

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  margin: 0 auto 10px;
`

export const BrandMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0 auto 10px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.action.primary.bg};
  color: ${({ theme }) => theme.colors.action.primary.text};
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`

export const BrandName = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  text-align: center;
  text-transform: none;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Title = styled.h1`
  margin: 0 0 4px;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const Subtitle = styled.p`
  margin: 0 0 14px;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const TrustNote = styled.p`
  margin: -4px 0 14px;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? theme.colors.backgroundSecondary
      : theme.colors.borderSubtle};
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.45;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const Progress = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

export const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const ProgressDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${({ $active, $done, theme }) => {
    if ($active || $done) return theme.colors.action.primary.bg
    return theme.colors.borderStrong
  }};
  opacity: ${({ $active, $done }) => ($active || $done ? 1 : 0.5)};
  transform: scale(${({ $active }) => ($active ? 1.25 : 1)});
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
`

export const ProgressLine = styled.span`
  width: 32px;
  height: 2px;
  border-radius: 999px;
  background: ${({ $done, theme }) =>
    $done ? theme.colors.action.primary.bg : theme.colors.borderDefault};
  transition: background 0.15s ease;
`

export const ProgressLabel = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
  animation: ${fadeUp} 0.3s ease-out both;
`

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
  width: 100%;

  button {
    width: 100%;
  }
`

export const SecondaryRow = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;

  button {
    flex: 1;
  }
`

export const SkipButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textSecondary};
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? theme.colors.backgroundSecondary
        : theme.colors.borderSubtle};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const NoticeWrap = styled.div`
  margin-bottom: 0.5rem;

  & > div {
    margin: 0;
  }
`

export const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.25rem;
  width: 100%;
  max-width: 15.5rem;
  margin: 0 auto;
`

export const DayButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  min-height: 1.65rem;
  padding: 0;
  border-radius: 6px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? theme.colors.action.primary.bg
        : theme.colors.borderDefault};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : theme.colors.background};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.text : theme.colors.textPrimary};
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $active, theme }) =>
      $active
        ? theme.colors.action.primary.bgHover
        : `${theme.colors.action.primary.bg}55`};
    background: ${({ $active, theme }) =>
      $active ? theme.colors.action.primary.bgHover : theme.colors.background};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const SetupProgress = styled.div`
  margin: 0.35rem 0 1rem;
`

export const SetupProgressBar = styled.div`
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(15, 23, 42, 0.08)'};
`

export const SetupProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => `${Math.min(Math.max($progress, 0), 1) * 100}%`};
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.action.primary.bg};
  transition: width 0.55s cubic-bezier(0.22, 1, 0.36, 1);
`

export const SetupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`

const itemIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const markPop = keyframes`
  0% { transform: scale(0.72); }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); }
`

export const SetupItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: ${({ $active, $done }) => ($active || $done ? 600 : 500)};
  line-height: 1.35;
  border: 1px solid
    ${({ $done, $active, theme }) => {
      if ($active) return `${theme.colors.action.primary.bg}55`
      if ($done) return `${theme.colors.action.primary.bg}33`
      return 'transparent'
    }};
  background: ${({ $done, $active, theme }) => {
    if ($active) {
      return theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.12)'
        : 'rgba(62, 207, 142, 0.1)'
    }
    if ($done) {
      return theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.06)'
        : 'rgba(62, 207, 142, 0.05)'
    }
    return theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(15, 23, 42, 0.03)'
  }};
  color: ${({ $done, $active, theme }) => {
    if ($done || $active) return theme.colors.textPrimary
    return theme.colors.textMuted
  }};
  opacity: ${({ $done, $active }) => ($done || $active ? 1 : 0.55)};
  transform: ${({ $active }) => ($active ? 'scale(1.01)' : 'scale(1)')};
  transition:
    background 0.35s ease,
    border-color 0.35s ease,
    color 0.35s ease,
    opacity 0.35s ease,
    transform 0.35s ease,
    box-shadow 0.35s ease;
  animation: ${itemIn} 0.4s ease both;
`

export const SetupMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 999px;
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  border: 1.5px solid
    ${({ $done, $active, theme }) => {
      if ($active) return 'transparent'
      if ($done) return theme.colors.action.primary.bg
      return theme.colors.borderStrong
    }};
  background: ${({ $done, $active, theme }) => {
    if ($done) return theme.colors.action.primary.bg
    if ($active) return 'transparent'
    return 'transparent'
  }};
  color: ${({ $done, $active, theme }) => {
    if ($done) return theme.colors.action.primary.text
    if ($active) return theme.colors.action.primary.bg
    return theme.colors.textMuted
  }};
  ${({ $done }) =>
    $done &&
    css`
      animation: ${markPop} 0.45s ease;
    `};
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const SetupPulse = styled.span`
  box-sizing: border-box;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  border: 2.5px solid ${({ theme }) => `${theme.colors.action.primary.bg}30`};
  border-top-color: ${({ theme }) => theme.colors.action.primary.bg};
  animation: ${spin} 0.7s linear infinite;
`

export const SetupStatus = styled.p`
  margin: 0.85rem 0 0;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.45;
  text-align: center;
  color: ${({ $ready, theme }) =>
    $ready ? theme.colors.action.primary.bg : theme.colors.textSecondary};
  transition: color 0.3s ease;
`

